import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import RawHtml from 'common/RawHtml';
import { withStyles } from 'theme/withStyles';
import { connect } from 'react-redux';
import visuallyHidden from 'styles/visuallyHidden';

export const styles = ({ forms, palette, spacing, typography }) => ({
  fieldOptionalLabelSuffix: {
    display: 'inline-block',
    fontSize: 14,
    padding: [0, spacing.unit],
  },
  hasIcon: {
    paddingRight: 21,
  },
  hasIconAndToolltip: {
    paddingRight: 45,
  },
  hasTooltip: {
    paddingRight: 21,
  },
  root: {
    '& *:not(small)': {
      display: 'inline',
      margin: 0,
      padding: 0,
    },
    '& small': {
      ...typography.p,
      ...typography.p3,
      color: ({ error }) => (error ? palette.error : typography.p3.color),
      marginLeft: 4,
    },
    '&.touched': {
      '& $fieldOptionalLabelSuffix': {
        fontSize: forms.label.fontSizeFocused,
      },
      background: 'transparent',
      color: ({ error }) => (error ? palette.error : palette.form.active),
      fontSize: forms.label.fontSizeFocused,
      fontWeight: typography.fontWeights.regular,
      paddingRight: 0,
      textTransform: 'uppercase',
      top: -10,
    },
    background: palette.background,
    color: ({ error }) => (error ? palette.error : palette.form.placeholder),
    fontFamily: typography.p.fontFamily,
    fontSize: forms.label.fontSizeNormal,
    fontWeight: typography.fontWeights.regular,
    left: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    position: 'absolute',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    top: 10,
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
    width: '100%',
    zIndex: 1,
  },
  visuallyHidden,
});

const FlyupLabel = ({
  className,
  classes,
  error,
  fieldDescr,
  fieldOptionalLabelSuffix,
  focus,
  hasIcon,
  hideFieldDescr,
  hideFieldLabel,
  htmlFor,
  isOffersPage,
  label,
  required,
  tooltip,
  value,
  visuallyHideLabelText,
}) => (
  <label
    type="label"
    className={classnames(
      'flyup',
      className,
      {
        hasError: error,
        touched: value || value === 0 || focus,
        [classes.hasTooltip]: !!tooltip && !hasIcon,
        [classes.hasIcon]: hasIcon && !tooltip,
        [classes.hasIconAndToolltip]: !!tooltip && hasIcon,
      },
      classes.root,
    )}
    htmlFor={htmlFor}
  >
    {hideFieldLabel ? null : (
      <RawHtml
        type="span"
        className={classnames({ [classes.visuallyHidden]: visuallyHideLabelText })}
      >
        {label}
      </RawHtml>
    )}
    {!required && !isOffersPage && (
      <RawHtml type="small" className={classes.fieldOptionalLabelSuffix}>
        {` ${fieldOptionalLabelSuffix || '(Optional)'}`}
      </RawHtml>
    )}
    {hideFieldDescr ? null : fieldDescr && <RawHtml type="small">{fieldDescr}</RawHtml>}
  </label>
);

FlyupLabel.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  error: PropTypes.bool,
  fieldDescr: PropTypes.string,
  fieldOptionalLabelSuffix: PropTypes.string,
  focus: PropTypes.bool,
  hasIcon: PropTypes.bool,
  hideFieldDescr: PropTypes.bool,
  hideFieldLabel: PropTypes.bool,
  htmlFor: PropTypes.string,
  isOffersPage: PropTypes.bool.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  tooltip: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  visuallyHideLabelText: PropTypes.bool,
};

FlyupLabel.defaultProps = {
  className: '',
  error: false,
  fieldDescr: '',
  fieldOptionalLabelSuffix: '',
  focus: false,
  hasIcon: false,
  hideFieldDescr: undefined,
  hideFieldLabel: undefined,
  htmlFor: '',
  label: '',
  required: true,
  tooltip: '',
  value: '',
  visuallyHideLabelText: false,
};

const StyledFlyupLabel = withStyles(styles)(FlyupLabel);

const mapStateToProps = (
  { fields, page, resources, ui },
  { fieldName, focus, hideFieldDescr, hideFieldLabel },
) => {
  const field = fields[fieldName];
  const isOffersPage = page.name === 'offers';
  return {
    fieldDescr: field?.fieldDescr,
    fieldOptionalLabelSuffix: resources.fieldOptionalLabelSuffix,
    focus: focus ?? ui.fieldFocus === fieldName,
    hideFieldDescr,
    hideFieldLabel,

    /** HACK: The backend can't properly send us required/not required on this page */
    isOffersPage,
    label: field?.label,
  };
};

export default connect(mapStateToProps)(StyledFlyupLabel);
